import type { Directive, DirectiveBinding } from "vue";
import { getObserverOptions, getLowQualityImageUrl, getBackgroundImageUrl, createHighQualityLoader, isSameSrc, isValidImageUrl } from './utils';

const stateMap = new WeakMap<
	HTMLElement,
	{
		src: string;
		observer: IntersectionObserver | null;
		cleanup?: () => void;
	}
>();

const cleanup = (el: HTMLElement) => {
	const state = stateMap.get(el);
	if (state) {
		state.observer?.disconnect();
		state.cleanup?.();
	}
	stateMap.delete(el);
};

const loadBackground = (el: HTMLElement, src: string) => {
	el.style.backgroundImage = `url(${getLowQualityImageUrl(src)})`;

	const observer = new IntersectionObserver((entries) => {
		if (entries[0].isIntersecting) {
			const cleanupLoader = createHighQualityLoader(
				src,
				() => {
					el.style.backgroundImage = `url(${src})`;
					cleanup(el);
				},
				() => cleanup(el)
			);

			const state = stateMap.get(el);
			if (state) {
				state.cleanup = cleanupLoader;
			}
		}
	}, getObserverOptions());

	observer.observe(el);
	stateMap.set(el, { src, observer });
};

const handleLazyBackground = (el: HTMLElement, binding: DirectiveBinding) => {
	const isEnabled = binding.value !== false;
	const newSrc = getBackgroundImageUrl(el);

	// 严格验证 URL
	if (!isValidImageUrl(newSrc)) {
		cleanup(el);
		return;
	}

	const state = stateMap.get(el);

	if (state && isSameSrc(state.src, newSrc) && isEnabled) {
		return;
	}

	cleanup(el);

	if (!isEnabled) {
		return;
	}

	loadBackground(el, newSrc);
};

const lazyBackground: Directive = {
	mounted: handleLazyBackground,
	updated: handleLazyBackground,
	unmounted: cleanup
};

export default lazyBackground;
